import React from 'react';
import { Box, styled, useTheme } from '@mui/material';
import {
  factorValueStyle,
  normalizedAverageDataValue,
  normalizedFactorValue,
} from './range-graph-utils';

export const VerticalCFRangeGraph = ({ min, max, factor, average }: any) => {
  const { config } = useTheme();

  const avgDataVal = normalizedAverageDataValue(average, min, max);
  const factorDataVal = normalizedFactorValue(factor, min, max);
  const factorStyle = factorValueStyle(average, factor, config);

  return (
    <StandardLine>
      <CFFactorsLine max={max} min={min}>
        <MinMaxNumber max={max}>{max}</MinMaxNumber>

        <MinMaxNumber min={min}>{min}</MinMaxNumber>
        <CFFactorTriangle
          factorDataVal={factorDataVal}
          factorStyle={factorStyle}
        />

        <CFFactorValue factorDataVal={factorDataVal} factorStyle={factorStyle}>
          {factor}
        </CFFactorValue>

        <IndustryAverageTriangle
          avgDataVal={avgDataVal}
          factorStyle={{ color: config.colors.azureBlue }}
        />

        <IndustryAverageValue
          avgDataVal={avgDataVal}
          factorStyle={{ color: config.colors.azureBlue }}
        >
          {average}
        </IndustryAverageValue>
      </CFFactorsLine>
    </StandardLine>
  );
};

// Range Graph styling
const StandardLine = styled(Box)(({ theme }) => {
  return {
    height: '100%',

    backgroundColor: theme.palette.background.modal,
    width: '0.5rem',
    borderRadius: 100,
    margin: 'auto',
    position: 'relative',
  };
});

const CFFactorsLine = styled<any>(Box)(({ theme, max, min }) => {
  return {
    height: max - min > 100 ? '100%' : `${max - min}%`,

    backgroundColor: theme.config.colors.mediumGrey,
    width: '0.5rem',
    borderRadius: 100,
    margin: 'auto',
    position: 'absolute',
    top: max > 100 ? 0 : `${100 - max}%`,
  };
});

const MinMaxNumber = styled<any>(Box)(({ theme, min, max }) => {
  const factorDigits = max?.toString()?.length || min?.toString()?.length;
  const baseStyle = {
    position: 'absolute',
    // eslint-disable-next-line
    left: factorDigits === 1 ? '-25%' : factorDigits === 3 ? '-125%' : '-75%',
    fontWeight: 500,
    fontSize: '1rem',

    color: theme.config.colors.mediumGrey,
  };

  if (max) {
    return {
      bottom: '105%',
      ...baseStyle,
    };
  }

  if (min) {
    return {
      top: '105%',
      ...baseStyle,
    };
  }

  return {};
});

const TriangleBase = styled(Box)({
  position: 'absolute',
  width: 0,
  height: 0,
  display: 'inline-block',
  borderTop: '7px solid transparent',
  borderBottom: '7px solid transparent',
});

const CFFactorTriangle = styled<any>(TriangleBase)(
  ({ factorDataVal, factorStyle }) => {
    return {
      bottom: `${factorDataVal}%`,
      transform: 'translateY(50%)',
      left: '-100%',
      borderLeft: `7px solid ${factorStyle.color}`,
      borderRight: '7px solid transparent',
    };
  }
);

const IndustryAverageTriangle = styled<any>(TriangleBase)(
  ({ theme, avgDataVal, factorStyle }) => {
    return {
      bottom: `${avgDataVal}%`,
      transform: 'translateY(50%)',
      right: '-100%',

      borderRight: `7px solid ${factorStyle.color}`,
      borderLeft: '7px solid transparent',
    };
  }
);

const CFFactorValue = styled<any>(Box)(({ factorDataVal, factorStyle }) => {
  return {
    position: 'absolute',
    bottom: `calc(${factorDataVal}% - 12px)`,
    color: factorStyle.color,
    left: '-400%',
    fontWeight: 500,
    fontSize: '1.2rem',
  };
});

const IndustryAverageValue = styled<any>(Box)(
  ({ theme, avgDataVal, factorStyle }) => {
    return {
      position: 'absolute',
      bottom: `calc(${avgDataVal}% - 12px)`,
      color: factorStyle.color,
      right: '-400%',
      fontWeight: 500,
      fontSize: '1.2rem',
    };
  }
);
